import { Box, Card, CardActionArea, Chip, Typography } from '@mui/material';
import { CalendarTodayOutlined, RoomOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPublication } from '../types/publication.types';
import { StatusBadge } from '@/shared/components/StatusBadge';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';

interface PublicationCardProps {
  publication: UserPublication;
  testId?: string;
}

export function PublicationCard({ publication, testId }: Readonly<PublicationCardProps>) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const firstProduct = publication.products[0];
  const firstPhoto = firstProduct?.photos[0];
  const productStatus = firstProduct?.status;
  const unit = firstProduct?.unit;

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  const getUnitLabel = (u: { labelFr: string; labelEn: string }): string => {
    return i18n.language === 'fr' ? u.labelFr : u.labelEn;
  };

  const getAvailability = (): string => {
    if (!publication.startDate) return '';
    const from = formatDate(publication.startDate);
    if (publication.endDate) {
      return t('home.publications.fromTo', { from, to: formatDate(publication.endDate) });
    }
    return t('home.publications.from', { date: from });
  };

  return (
    <Card
      sx={{ borderRadius: 2, boxShadow: 1 }}
      data-testid={testId ?? `publication-card-${publication.id}`}
    >
      <CardActionArea
        onClick={() =>
          navigate(`/offers/${publication.id}`, { state: { from: 'my-publications' } })
        }
      >
        <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
          {/* Thumbnail */}
          <Box
            sx={{
              width: 90,
              height: 90,
              flexShrink: 0,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={firstPhoto?.thumbnailUrl || firstPhoto?.url || PLACEHOLDER_IMAGES.product}
              alt={firstProduct?.name ?? publication.title}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = PLACEHOLDER_IMAGES.product;
              }}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>

          {/* Details */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Offer title */}
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {publication.title}
            </Typography>

            {/* Product name */}
            {(firstProduct?.material?.subcategory ?? firstProduct?.name) && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {firstProduct?.material?.subcategory ?? firstProduct?.name}
              </Typography>
            )}

            {/* Location */}
            {publication.address && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RoomOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {publication.address}
                </Typography>
              </Box>
            )}

            {/* Quantity */}
            {firstProduct && unit && (
              <Typography variant="body2" color="text.secondary">
                {t('home.publications.quantity', {
                  value: firstProduct.quantity,
                  unit: getUnitLabel(unit),
                })}
              </Typography>
            )}

            {/* Status badge */}
            {productStatus && (
              <Box sx={{ mt: 0.5 }}>
                <StatusBadge status={productStatus} size="small" />
              </Box>
            )}

            {/* Offer closed badge */}
            {publication.status === 'CLOSED' && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={
                    publication.closureReason === 'VENTE_CONCLUE'
                      ? t('myPublications.status.closedDealConcluded')
                      : t('myPublications.status.closed')
                  }
                  size="small"
                  sx={{ bgcolor: 'error.main', color: 'error.contrastText', fontWeight: 600 }}
                  data-testid={`publication-card-closed-badge-${publication.id}`}
                />
              </Box>
            )}

            {/* Availability */}
            {getAvailability() && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {getAvailability()}
                </Typography>
              </Box>
            )}

            {/* Publication date */}
            <Typography variant="caption" color="text.secondary">
              {t('home.publications.publishedOn', { date: formatDate(publication.createdAt) })}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
